import { useMemo } from "react";
import classnames from "classnames";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";
import qs, { ParsedQs } from "qs";

import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";

import Dropdown from "components/botanic/Dropdown";
import DropdownItem from "components/botanic/Dropdown/DropdownItem";

const SortDropdown = ({
  availableSorts,
  eventCategory,
  sortDisplayStrings,
  onToggleDropdown,
  sortsAsLinks,
}: {
  availableSorts: string[];
  eventCategory: Category;
  sortDisplayStrings: Record<string, string>;
  onToggleDropdown?: () => void;
  sortsAsLinks?: boolean;
}) => {
  const { asPath } = useRouter();
  const { query } = parseNextRouterAsPath<ParsedQs>(asPath);
  const { publishEvent } = useEventTracker();
  const { selectFilterValue, addFilter } = useFilters({
    shallowRouting: false,
  });

  const validAvailableSorts = useMemo(
    () =>
      availableSorts.filter((value) =>
        Object.keys(sortDisplayStrings).includes(value),
      ),
    [availableSorts, sortDisplayStrings],
  );

  if (validAvailableSorts.length === 0) {
    return <></>;
  }

  const sortValue = selectFilterValue("sort") || validAvailableSorts[0];
  const pathWithoutQuery = asPath.split("?")[0];
  const { sort: _oldSort, page: _oldPage, ...rest } = query;

  const handleClick = (value: string) => {
    // If we're loading these as buttons, we need to include the filters when we handle click
    !sortsAsLinks && addFilter({ sort: value });

    publishEvent({
      action: Action.click,
      category: eventCategory,
      label: sortDisplayStrings[value].toLowerCase(),
    });
  };

  return (
    <div
      data-testid="sort-dropdown"
      className="flex flex-shrink-0 items-center justify-center text-xs ml-lg"
    >
      Sort by&nbsp;
      <Dropdown
        className="items-center"
        selectedItem={sortDisplayStrings[sortValue as string]}
        onToggleDropdown={onToggleDropdown}
      >
        <>
          {(validAvailableSorts || []).map((value) => {
            const orderedParams = {
              sort: value,
              ...rest,
            };

            const isHighestRatedWithNoFilters =
              value === "highest-rated" && !query.filter && !query.sort;

            const href = sortsAsLinks
              ? `${pathWithoutQuery}?${qs.stringify(orderedParams, { encode: false })}`
              : undefined;

            return (
              <DropdownItem
                key={`sort-${value}`}
                className={classnames("p-md z-10 whitespace-nowrap", {
                  "font-extrabold": sortValue === value,
                })}
                data-testid={`listing-sort-${value}`}
                onClick={() => handleClick(value)}
                noFollow={!isHighestRatedWithNoFilters}
                href={href}
              >
                {sortDisplayStrings[value]}
              </DropdownItem>
            );
          })}
        </>
      </Dropdown>
    </div>
  );
};

export default SortDropdown;
